/**
 * Decision Math — single source of truth for LotLingo financial calculations.
 *
 * Consumed by the portal (Meat on the Bone, walk-away range, bid position),
 * the API (notification rules, server-side threshold evaluation), and the
 * extension (in-page overlay).
 *
 * Money model (matches the original portal implementation — additive fees):
 *   Spend(bid)  = bid · (1 + taxRate + buyerPremium) + fixedLotCost
 *   NetResale   = estimate · (1 − sellerCosts)
 *   Meat        = NetResale − Spend(bid)
 *
 * Walk-away range (inverse of the meat equation, solved for bid):
 *   stayUnderBid:   highest bid where meat on the LOW estimate still meets the
 *                   target margin (profit ≥ targetMargin · spend). Below this,
 *                   even a pessimistic flip clears the user's bar.
 *   neverExceedBid: bid where meat on the HIGH estimate reaches zero. Past
 *                   this, even the optimistic case loses money.
 *   Between the two is the judgment zone.
 *
 * @module shared/decisionMath
 */

import { roundToTwo } from './currencyUtils.js';

/** Effective fee inputs for a single lot (already resolved from user defaults + lot overrides). */
export interface FeeBreakdown {
  /** Sales tax as a percentage, e.g. 7.125 for 7.125%. */
  taxRatePct: number;
  /** Buyer's premium as a percentage, e.g. 15 for 15%. */
  buyerPremiumPct: number;
  /** Selling costs (marketplace fees etc.) as a percentage of resale price. */
  sellerCostsPct: number;
  /** Fixed per-lot cost in dollars (handling, pickup mileage, ...). */
  fixedLotCost: number;
}

/** Default fee values, mirroring the portal's historical defaults. */
export const DEFAULT_FEES: FeeBreakdown = {
  taxRatePct: 0,
  buyerPremiumPct: 15,
  sellerCostsPct: 14,
  fixedLotCost: 0,
};

/** Default target margin (percent of spend) for the stay-under ceiling. */
export const DEFAULT_TARGET_MARGIN_PCT = 30;

/** Per-lot fee overrides; undefined/null fields fall back to user defaults. */
export interface LotFeeOverrides {
  taxRatePct?: number | null;
  buyerPremiumPct?: number | null;
  sellerCostsPct?: number | null;
  fixedLotCost?: number | null;
}

export interface MeatResult {
  /** Profit if the item resells at the low estimate; null when no low estimate. */
  meatLow: number | null;
  /** Profit if the item resells at the high estimate; null when no high estimate. */
  meatHigh: number | null;
  /** Total out-of-pocket spend at the given bid. */
  spendAmount: number;
}

export interface WalkAwayRange {
  /**
   * Highest bid where the LOW estimate still clears the target margin.
   * Null when there is no low estimate. Clamped to ≥ 0.
   */
  stayUnderBid: number | null;
  /**
   * Bid where the HIGH estimate yields zero profit ("never exceed").
   * Null when there is no high estimate. Clamped to ≥ 0.
   */
  neverExceedBid: number | null;
  /** Target margin (% of spend) used to derive stayUnderBid. */
  targetMarginPct: number;
}

export type BidPosition = 'safe' | 'judgment' | 'over';

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function pctToMultiplier(pct: number): number {
  return pct / 100;
}

/** Resolve effective fees from user defaults plus optional per-lot overrides. */
export function resolveFees(
  defaults: Partial<FeeBreakdown> | null | undefined,
  overrides?: LotFeeOverrides | null
): FeeBreakdown {
  const pick = (override: number | null | undefined, fallback: number | undefined, def: number) => {
    if (isFiniteNumber(override)) return override;
    if (isFiniteNumber(fallback)) return fallback;
    return def;
  };
  return {
    taxRatePct: pick(overrides?.taxRatePct, defaults?.taxRatePct, DEFAULT_FEES.taxRatePct),
    buyerPremiumPct: pick(
      overrides?.buyerPremiumPct,
      defaults?.buyerPremiumPct,
      DEFAULT_FEES.buyerPremiumPct
    ),
    sellerCostsPct: pick(
      overrides?.sellerCostsPct,
      defaults?.sellerCostsPct,
      DEFAULT_FEES.sellerCostsPct
    ),
    fixedLotCost: pick(overrides?.fixedLotCost, defaults?.fixedLotCost, DEFAULT_FEES.fixedLotCost),
  };
}

/**
 * Total out-of-pocket spend for a winning bid.
 * Spend = bid · (1 + tax + premium) + fixedLotCost
 */
export function computeSpend(bid: number, fees: FeeBreakdown): number {
  return roundToTwo(
    bid +
      bid * pctToMultiplier(fees.taxRatePct) +
      bid * pctToMultiplier(fees.buyerPremiumPct) +
      fees.fixedLotCost
  );
}

/** Net proceeds after selling costs for a given resale price. */
export function computeNetResale(estimate: number, fees: FeeBreakdown): number {
  return roundToTwo(estimate - estimate * pctToMultiplier(fees.sellerCostsPct));
}

/**
 * "Meat on the Bone": profit range at a given bid.
 * Pass null for estimates that are unknown.
 */
export function computeMeat(
  bid: number,
  lowEstimate: number | null | undefined,
  highEstimate: number | null | undefined,
  fees: FeeBreakdown
): MeatResult {
  const spendAmount = computeSpend(bid, fees);
  const meatLow = isFiniteNumber(lowEstimate)
    ? roundToTwo(computeNetResale(lowEstimate, fees) - spendAmount)
    : null;
  const meatHigh = isFiniteNumber(highEstimate)
    ? roundToTwo(computeNetResale(highEstimate, fees) - spendAmount)
    : null;
  return { meatLow, meatHigh, spendAmount };
}

/**
 * Invert the spend equation: find the bid that produces a desired total spend.
 * bid = (spend − fixedLotCost) / (1 + tax + premium)
 * Returns null when the multiplier is non-positive (degenerate fee input).
 */
function bidForSpend(spend: number, fees: FeeBreakdown): number | null {
  const multiplier = 1 + pctToMultiplier(fees.taxRatePct) + pctToMultiplier(fees.buyerPremiumPct);
  if (multiplier <= 0) return null;
  const bid = (spend - fees.fixedLotCost) / multiplier;
  return roundToTwo(Math.max(0, bid));
}

/**
 * Compute the walk-away range for a lot.
 *
 * stayUnderBid:   meat(low estimate) = targetMargin · spend
 *                 ⇒ spend = netResale(low) / (1 + targetMargin)
 * neverExceedBid: meat(high estimate) = 0
 *                 ⇒ spend = netResale(high)
 *
 * Both are independent of the current bid, so they remain valid between
 * estimate/fee changes — portable to overlays and notifications.
 */
export function computeWalkAwayRange(
  lowEstimate: number | null | undefined,
  highEstimate: number | null | undefined,
  fees: FeeBreakdown,
  targetMarginPct: number = DEFAULT_TARGET_MARGIN_PCT
): WalkAwayRange {
  const margin = isFiniteNumber(targetMarginPct) && targetMarginPct >= 0 ? targetMarginPct : DEFAULT_TARGET_MARGIN_PCT;

  let stayUnderBid: number | null = null;
  if (isFiniteNumber(lowEstimate)) {
    const targetSpend = computeNetResale(lowEstimate, fees) / (1 + pctToMultiplier(margin));
    stayUnderBid = bidForSpend(targetSpend, fees);
  }

  let neverExceedBid: number | null = null;
  if (isFiniteNumber(highEstimate)) {
    neverExceedBid = bidForSpend(computeNetResale(highEstimate, fees), fees);
  }

  // Guard inverted ranges (low estimate above high, extreme margins, ...):
  // the ceiling can never sit below the floor of the judgment zone.
  if (stayUnderBid != null && neverExceedBid != null && stayUnderBid > neverExceedBid) {
    stayUnderBid = neverExceedBid;
  }

  return { stayUnderBid, neverExceedBid, targetMarginPct: margin };
}

/**
 * Classify a bid against the walk-away range.
 * Returns null when the range has neither boundary.
 * With a missing boundary the classification is conservative: anything not
 * provably safe or provably over lands in 'judgment'.
 */
export function classifyBidPosition(bid: number, range: WalkAwayRange): BidPosition | null {
  const { stayUnderBid, neverExceedBid } = range;
  if (stayUnderBid == null && neverExceedBid == null) return null;
  if (stayUnderBid != null && bid <= stayUnderBid) return 'safe';
  if (neverExceedBid != null && bid > neverExceedBid) return 'over';
  return 'judgment';
}
